import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import InviteUserModal from '../../components/tenant/InviteUserModal';
import { useUsers, User } from '../../hooks/useUsers';
import { useTenantRoles, TenantRole } from '../../hooks/useTenants';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';
import {
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

const TenantUsers: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, currentTenant, hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Use hooks for data fetching
  const {
    users: tenantUsers,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    refresh,
    updateUser,
    deleteUser,
    updateUserRole,
    terminateUserSessions,
  } = useUsers({
    tenantId: currentTenant?.id,
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  const { roles: tenantRoles, loading: rolesLoading } = useTenantRoles(currentTenant?.id || '');

  // Get unique roles for filter
  const availableRoles = useMemo(() => {
    return tenantRoles.map(role => role.name);
  }, [tenantRoles]);

  // Filter users (additional client-side filtering if needed)
  const filteredUsers = useMemo(() => {
    return tenantUsers.filter(user => {
      const userTenant = (user.tenants ?? []).find(t => t.tenantId === currentTenant?.id);
      if (!userTenant) return false;

      // Role filter (if not already handled by API)
      if (roleFilter !== 'all' && userTenant.role !== roleFilter) {
        return false;
      }

      return true;
    });
  }, [tenantUsers, roleFilter, currentTenant]);

  const getUserRole = (user: User): TenantRole | undefined => {
    const userTenant = (user.tenants ?? []).find(t => t.tenantId === currentTenant?.id);
    return userTenant ? tenantRoles.find(r => r.name === userTenant.role) : undefined;
  };

  // Handle search term changes with debouncing
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    // Update filters with a small delay to avoid too many API calls
    setTimeout(() => {
      setFilters({
        ...filters,
        search: newSearchTerm || undefined,
        page: 1, // Reset to first page on search
      });
    }, 300);
  };

  // Handle status filter changes
  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setFilters({
      ...filters,
      status: newStatus !== 'all' ? newStatus : undefined,
      page: 1, // Reset to first page on filter change
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50';
      case 'inactive':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const canManageUsers = hasPermission('users.write') || hasPermission('tenant.users.manage');

  return (
    <ProtectedRoute requiredPermission="tenant.users.view">
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Tenant Users</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Manage users and permissions for {currentTenant?.name}
                </p>
              </div>
              {canManageUsers && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Invite User
                </button>
              )}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  showFilters
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-foreground hover:bg-accent'
                } border border-border`}
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
                <ChevronDownIcon className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="bg-card p-4 rounded-lg border border-border space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Role
                    </label>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="all">All Roles</option>
                      {availableRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-card">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Joined
                    </th>
                    {canManageUsers && (
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={canManageUsers ? 6 : 5} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          <span className="ml-2 text-muted-foreground">Loading users...</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={canManageUsers ? 6 : 5} className="px-6 py-12 text-center">
                        <div className="text-red-600">
                          <p className="font-medium">Error loading users</p>
                          <p className="text-sm mt-1">{error}</p>
                          <button
                            onClick={refresh}
                            className="mt-2 text-sm text-primary hover:text-primary/80 underline"
                          >
                            Try again
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={canManageUsers ? 6 : 5} className="px-6 py-12 text-center">
                        <div className="text-muted-foreground">
                          <UserIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p className="font-medium">No users found</p>
                          <p className="text-sm mt-1">
                            {searchTerm || statusFilter !== 'all' || roleFilter !== 'all'
                              ? 'Try adjusting your search or filters'
                              : 'Get started by inviting your first user'
                            }
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => {
                      const role = getUserRole(user);
                    const userTenant = (user.tenants ?? []).find((t: any) => t.tenantId === currentTenant?.id) as any;
                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/tenant/users/${user.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-muted-foreground" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {user.name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <ShieldCheckIcon className="h-4 w-4 text-muted-foreground mr-2" />
                            <span className="text-sm text-foreground">{role?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status ?? 'active')}`}>
                            {getStatusIcon(user.status ?? 'active')}
                            <span className="ml-1 capitalize">{user.status ?? 'active'}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {userTenant?.joinedAt ? new Date(userTenant.joinedAt).toLocaleDateString() : 'N/A'}
                        </td>
                        {canManageUsers && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle actions menu
                              }}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
          </div>

          {/* Summary Stats */}
          {filteredUsers.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center">
                  <UserIcon className="h-8 w-8 text-primary" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-semibold text-foreground">{filteredUsers.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-muted-foreground">Active</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {filteredUsers.filter(u => u.status === 'ACTIVE').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center">
                  <ExclamationCircleIcon className="h-8 w-8 text-yellow-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-muted-foreground">Pending</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {filteredUsers.filter(u => u.status === 'PENDING').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-8 w-8 text-primary" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-muted-foreground">Roles</p>
                    <p className="text-2xl font-semibold text-foreground">{availableRoles.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

        {/* Invite User Modal */}
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={async (data) => {
            if (!currentTenant) return;
            
            // Find the role ID from the selected role name
            const selectedRole = tenantRoles.find(role => role.name === data.role);
            if (!selectedRole) {
              console.error('Selected role not found');
              return;
            }

            // Call the API to invite the user
            try {
              const response = await apiClient.inviteUserToTenant(currentTenant.id, {
                email: data.email,
                roleId: selectedRole.id,
                message: data.customMessage,
              });

              if (response.success) {
                setShowInviteModal(false);
                await refresh(); // Refresh the user list
                toast.success(`Invitation sent to ${data.email}`);
              } else {
                toast.error(response.error || 'Failed to invite user');
              }
            } catch (error) {
              console.error('Error inviting user:', error);
              toast.error('Failed to invite user');
            }
          }}
        />
      </div>
    </ProtectedRoute>
  );
};

export default TenantUsers;